import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 他事業所連携メッセージ API 操作クラス
 */
export default class OtherOfficeOnlineMeetings {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET OtherOfficeOnlineMeetings
   */
  public async gets(userStore: LoginUser, queryParams?: { start: number; end: number } | undefined): Promise<Array<OtherOfficeOnlineMeetingGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeOnlineMeetings, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<OtherOfficeOnlineMeetingGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET OtherOfficeOnlineMeetings Count
   */
  public async getCount(userStore: LoginUser, queryParams?: { start: number; end: number } | undefined): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeOnlineMeetings, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### GET OtherOfficeOnlineMeetings
   */
  public async get(userStore: LoginUser, otherOfficeFacilityId: number, id: number): Promise<OtherOfficeOnlineMeetingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeOnlineMeetingById, formatParams);

    const response = await this.commonService.executeGet<OtherOfficeOnlineMeetingGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### POST OtherOfficeOnlineMeeting
   */
  public async post(userStore: LoginUser, otherOfficeFacilityId: number, requestBody: OtherOfficeOnlineMeetingPostModel): Promise<OtherOfficeOnlineMeetingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeOnlineMeeting, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH OtherOfficeOnlineMeeting
   */
  public async patch(userStore: LoginUser, otherOfficeFacilityId: number, id: number, requestBody: OtherOfficeOnlineMeetingPatchModel): Promise<OtherOfficeOnlineMeetingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeOnlineMeetingById, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE OtherOfficeOnlineMeeting
   */
  public async delete(userStore: LoginUser, otherOfficeFacilityId: number, id: number): Promise<OtherOfficeOnlineMeetingGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      otherOfficeFacilityId: otherOfficeFacilityId,
      id: id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeOnlineMeetingById, formatParams);
    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OtherOfficeOnlineMeetingGetModel {
  /** 施設ID（招待元施設のID） */
  mainFacilityId: number;
  /** システムで一意のID */
  id: number;
  /** 施設ID（招待先施設のID） */
  subFacilityId: number;
  /** 主催者か否か */
  isMain: boolean;
  /** 会議相手施設ID */
  targetFacilityId: number;
  /** 会議相手施設名 */
  targetFacilityName: string;
  /** 開始日時 */
  startDatetime: number;
  /** 終了日時 */
  endDatetime: number;
  /** 件名 */
  title: string;
  /** 内容 */
  text: string;
  /** 会議案内送付先メールアドレス */
  notificationMailAddress: string[];
  /** オンライン会議URL（mainFacilityId subFacilityId id createdを連結してハッシュ化） */
  onlineMeetingUrl: string;
  /** オンライン会議の部屋ID */
  roomId: string;

  /** 削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 */
  created: number;
  /** 更新日時 */
  updated: number;
}

export interface OtherOfficeOnlineMeetingPostModel {
  /** 開始日時 */
  startDatetime: number;
  /** 終了日時 */
  endDatetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
  /** 会議案内送付先メールアドレス */
  notificationMailAddress: string[];
}

export interface OtherOfficeOnlineMeetingPatchModel {
  /** 開始日時 */
  startDatetime: number;
  /** 終了日時 */
  endDatetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string;
  /** 会議案内送付先メールアドレス */
  notificationMailAddress: string[];
  /** 更新日時 */
  updated: number;
}
